import { Tooltip } from '@elipssolution/harfang';
import { Stack, Typography, styled } from '@mui/material';
import clsx from 'clsx';
import Image from 'next/image';
import { useMemo } from 'react';

import useFormat from '../../../../hooks/useFormat';
import useWidgetLogoSize from '../../../hooks/useWidgetLogoSize';

const ApplicationNameTypography = styled(Typography)({
	overflow: 'hidden',
	textOverflow: 'ellipsis',

	'&.isMobileFormat': {
		display: '-webkit-box',
		WebkitLineClamp: '2',
		WebkitBoxOrient: 'vertical',
		wordBreak: 'break-word',
	},

	'&:not(.isMobileFormat)': {
		whiteSpace: 'nowrap',
	},
});

const ImageContainer = styled(Stack, {
	shouldForwardProp: (prop) => prop !== 'logoSize',
})<{ logoSize: number }>(({ logoSize, theme: { shape } }) => ({
	width: logoSize,
	height: logoSize,
	flexShrink: 0,
	flexGrow: 0,
	borderRadius: shape.borderRadius * 4,
	overflow: 'hidden',

	'&.disabled': {
		filter: 'grayscale(1)',
	},

	'&.isMobileFormat': {
		borderRadius: shape.borderRadius,
	},
}));

const StyledImagePlaceholder = styled(Typography)(({ theme: { palette, shape } }) => ({
	flex: 1,
	borderWidth: 2,
	borderStyle: 'solid',
	borderColor: palette.primary.main,
	borderRadius: shape.borderRadius * 2,
	color: palette.primary.main,

	'&.isMobileFormat': {
		borderRadius: shape.borderRadius,
	},
}));

type ExternalApplicationCardProps = {
	name: string;
	imageSrc?: string;
	disabled?: boolean;
	disabledMessage?: string;
};

const ExternalApplicationCard = ({
	name,
	imageSrc,
	disabled = false,
	disabledMessage,
}: ExternalApplicationCardProps) => {
	const { isMobileFormat } = useFormat();
	const { logoSize } = useWidgetLogoSize();

	const tooltipContent = useMemo(() => {
		if (disabled && disabledMessage) {
			return disabledMessage;
		}

		return undefined;
	}, [disabled, disabledMessage]);

	return (
		<Tooltip content={tooltipContent}>
			<Stack alignItems="center" gap={1} padding={1}>
				<ImageContainer className={clsx({ disabled, isMobileFormat })} logoSize={logoSize}>
					{imageSrc ? (
						<Image src={imageSrc} alt={`Logo ${name}`} width={96} height={96} objectFit="contain" />
					) : (
						<StyledImagePlaceholder
							className={clsx({ isMobileFormat })}
							variant={isMobileFormat ? 'h5' : 'h3'}
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							{name.charAt(0)}
						</StyledImagePlaceholder>
					)}
				</ImageContainer>

				<ApplicationNameTypography className={clsx({ isMobileFormat })} align="center" width="100%">
					{name}
				</ApplicationNameTypography>
			</Stack>
		</Tooltip>
	);
};

export default ExternalApplicationCard;
